import { useCallback, useEffect, useMemo, useState } from 'react'

import { UniformGroup, UniformItem, getUniformGroups, getUniformItems, setUniformGroupItems } from '../../data/uniformData'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'

const sameIds = (a: number[], b: number[]) => a.length === b.length && a.every((id) => b.includes(id))

/**
 * Which items each group's members are offered. Choose a group, tick its items.
 * An item ticked for no group is for everyone; someone in several of an item's
 * groups is still offered it once.
 */
const UniformGroupsTab = ({ refreshKey }: { refreshKey: number }) => {
  const { showToast } = useToast()
  const [groups, setGroups] = useState<UniformGroup[]>([])
  const [items, setItems] = useState<UniformItem[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [ticked, setTicked] = useState<number[]>([])
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [g, i] = await Promise.all([getUniformGroups(), getUniformItems(true)])
      setGroups(g)
      setItems(i)
    } catch (error) {
      showToast(getApiErrorDetails(error, "Groups couldn't be loaded.").message, 'error')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  const selected = groups.find((g) => g.groupId === selectedId) ?? null
  const dirty = !!selected && !sameIds(ticked, selected.uniformItemIds)

  const choose = (group: UniformGroup) => {
    if (dirty && !window.confirm(`Discard your changes to ${selected?.name}?`)) return
    setSelectedId(group.groupId)
    setTicked(group.uniformItemIds)
  }

  const shownGroups = useMemo(() => {
    const term = search.trim().toLowerCase()
    return groups.filter((g) => !term || g.name.toLowerCase().includes(term))
  }, [groups, search])

  // How many groups each item is limited to, counting the ticks not yet saved.
  const groupCount = (itemId: number) =>
    groups.filter((g) => (g.groupId === selectedId ? ticked : g.uniformItemIds).includes(itemId)).length

  const save = async () => {
    if (!selected) return
    setSaving(true)
    try {
      const updated = await setUniformGroupItems(selected.groupId, ticked)
      setGroups((prev) => prev.map((g) => (g.groupId === updated.groupId ? updated : g)))
      setTicked(updated.uniformItemIds)
      showToast(`Uniform for ${updated.name} saved`, 'success')
    } catch (error) {
      showToast(getApiErrorDetails(error, "The group's uniform couldn't be saved.").message, 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <section className='uni-card uni-empty'>Loading groups…</section>

  return (
    <section className='uni-card uni-groups'>
      <div className='uni-groups__list'>
        <input
          className='k-input uni-groups__search'
          placeholder='Search groups'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {shownGroups.length === 0 ? (
          <div className='uni-empty'>No groups found.</div>
        ) : (
          shownGroups.map((g) => (
            <button
              key={g.groupId}
              type='button'
              className={`uni-groups__group${g.groupId === selectedId ? ' uni-groups__group--active' : ''}`}
              onClick={() => choose(g)}
            >
              <span className='uni-groups__name'>{g.name}</span>
              <span className='uni-muted'>
                {g.memberCount} {g.memberCount === 1 ? 'member' : 'members'} · {g.uniformItemIds.length}{' '}
                {g.uniformItemIds.length === 1 ? 'item' : 'items'}
                {g.groupType !== 'Security' ? ` · ${g.groupType}` : ''}
              </span>
            </button>
          ))
        )}
      </div>

      <div className='uni-groups__detail'>
        {!selected ? (
          <div className='uni-empty'>
            Choose a group to pick the uniform its members are offered. Items not chosen for any group are offered to
            everyone.
          </div>
        ) : (
          <>
            <div className='uni-panel__heading'>
              <div>
                <h3>{selected.name}</h3>
                <span className='uni-muted'>
                  Members are offered the ticked items. Someone in several groups sees each item once.
                </span>
              </div>
              <div className='uni-actions'>
                <button
                  type='button'
                  className='uni-btn uni-btn--secondary'
                  disabled={!dirty || saving}
                  onClick={() => setTicked(selected.uniformItemIds)}
                >
                  Undo
                </button>
                <button type='button' className='uni-btn uni-btn--primary' disabled={!dirty || saving} onClick={save}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>

            {items.length === 0 ? (
              <div className='uni-empty'>No uniform items have been set up yet.</div>
            ) : (
              <table className='uni-table'>
                <thead>
                  <tr>
                    <th style={{ width: '2.5rem' }} />
                    <th>Item</th>
                    <th>Offered to</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const on = ticked.includes(item.uniformItemId)
                    const count = groupCount(item.uniformItemId)
                    return (
                      <tr key={item.uniformItemId}>
                        <td>
                          <input
                            type='checkbox'
                            id={`uni-group-item-${item.uniformItemId}`}
                            checked={on}
                            onChange={() =>
                              setTicked((prev) =>
                                on ? prev.filter((id) => id !== item.uniformItemId) : [...prev, item.uniformItemId]
                              )
                            }
                          />
                        </td>
                        <td>
                          <label htmlFor={`uni-group-item-${item.uniformItemId}`} style={{ fontWeight: 600 }}>
                            {item.name}
                          </label>
                          {!item.isActive && <span className='uni-muted'> (hidden)</span>}
                        </td>
                        <td className='uni-muted'>
                          {count > 0
                            ? `${count} ${count === 1 ? 'group' : 'groups'}`
                            : item.limitedToGroups && item.groups.length === 0
                              ? 'Nobody - its groups were deleted'
                              : 'Everyone'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </section>
  )
}

export default UniformGroupsTab
